<template>
  <div>
    <v-container>
      <grid
        :items="listSeo"
        :headers="seoHeaders"
        type="seo"
        :loading="loading"
        @addSEO="openAdd"
        @updateSEO="openEdit"
      />
    </v-container>

    <!-- One dialog for add and edit: it always opens with the page it is for (or empty). -->
    <v-dialog v-model="dialog" max-width="520" :persistent="saving">
      <v-card>
        <v-card-title class="text-h6">{{ isEdit ? 'Edit SEO' : 'Add SEO' }}</v-card-title>
        <v-card-text class="pt-2">
          <v-form ref="form" @submit.prevent="save">
            <v-text-field
              v-model="seo.name"
              label="Page name"
              outlined
              :rules="[v => !!(v && String(v).trim()) || 'Page name is required']"
            ></v-text-field>

            <v-text-field
              v-model="seo.title"
              label="SEO title"
              outlined
              counter="500"
              :rules="[v => !!(v && String(v).trim()) || 'SEO title is required', v => !v || v.length <= 500 || 'At most 500 characters']"
            ></v-text-field>

            <v-textarea
              v-model="seo.description"
              label="SEO description"
              outlined
              rows="3"
              auto-grow
              counter="500"
              :rules="[v => !v || v.length <= 500 || 'At most 500 characters']"
            ></v-textarea>

            <image-field v-model="seo.image" :current="currentImage" label="SEO image" />

            <v-text-field
              v-if="isEdit"
              v-model="seo.slug"
              label="Slug"
              outlined
              :rules="[v => !!(v && String(v).trim()) || 'Slug is required']"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn text :disabled="saving" @click="dialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed color="primary" :loading="saving" @click="save">{{ isEdit ? 'Save changes' : 'Add' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { settingSEOs, settingAddSEO, settingUpdateSEO, headers } from '../../links'
import grid from '../../components/tables.vue'
import ImageField from '../../components/ImageField.vue'

function emptySeo () {
  return { name: '', title: '', description: '', image: null, slug: '' }
}

export default {
  components: {
    grid,
    ImageField
  },
  data () {
    return {
      listSeo: [],
      loading: true,
      seoHeaders: [
        { text: 'Image', value: 'featured_image' },
        { text: 'Page Name', value: 'page_name' },
        { text: 'SEO Title', value: 'seo_title' },
        { text: 'Slug', value: 'slug' },
        { text: 'Edit', value: 'editSEO' }
      ],
      dialog: false,
      isEdit: false,
      id: null,
      seo: emptySeo(),
      currentImage: null, // URL of the saved image (edit)
      saving: false
    }
  },
  methods: {
    notify (color, text) {
      this.$root.$emit('tn-notify', { color, text })
    },
    // Every open starts from a clean form: nothing is left over from the previous page.
    open (isEdit, values, id, image) {
      this.isEdit = isEdit
      this.id = id
      this.seo = { ...emptySeo(), ...values }
      this.currentImage = image || null
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    openAdd () {
      this.open(false, {}, null, null)
    },
    openEdit (item) {
      this.open(true, {
        name: item.page_name || '',
        title: item.seo_title || '',
        description: item.seo_description || '',
        slug: item.slug || ''
      }, item.id, item.featured_image)
    },
    save () {
      if (!this.$refs.form.validate()) return
      const formData = new FormData()
      formData.append('page_name', this.seo.name.trim())
      formData.append('seo_title', (this.seo.title || '').trim())
      formData.append('seo_description', (this.seo.description || '').trim())
      if (this.isEdit) formData.append('slug', this.seo.slug.trim())
      if (this.seo.image) formData.append('featured_image', this.seo.image, this.seo.image.name)

      this.saving = true
      const url = this.isEdit ? settingUpdateSEO(this.id) : settingAddSEO
      this.$http.post(url, formData, { headers: headers(this.$cookies.get('userToken')) }).then(() => {
        this.saving = false
        this.dialog = false
        this.notify('success', this.isEdit ? 'SEO saved' : 'SEO added')
        this.getListofSEO()
      }, err => {
        this.saving = false
        this.notify('error', this.errorText(err))
      })
    },
    // The API answers 422 with { errors: { field: [message] } } and no `message`.
    errorText (err) {
      const body = (err && err.body) || {}
      const errors = body.errors && Object.values(body.errors)
      if (errors && errors.length) return [].concat(errors[0])[0]
      return body.message || 'Could not save the SEO page'
    },
    getListofSEO () {
      this.loading = true
      this.$http.get(settingSEOs, { headers: headers(this.$cookies.get('userToken')) }).then(response => {
        this.loading = false
        this.listSeo = response.body.data || []
      }, err => {
        this.loading = false
        this.notify('error', (err.body && err.body.message) || 'Could not load the SEO pages')
      })
    }
  },
  created () {
    this.getListofSEO()
  }
}
</script>
